import { CaretLeftIcon, CaretRightIcon, PlusIcon, SpeakerHighIcon, XIcon } from '@phosphor-icons/react'
import { forwardRef, memo, useEffect, useRef, useState } from 'react'
import { useTabsStore } from './store.ts'

interface TabBarProps {
  activeTabId: string
  playingTabId: string | null
  activeSidebarTrackId: string | null
  isPlaying: boolean
  onSwitchTab: (tabId: string) => void
  onCloseTab: (tabId: string) => void
  onNewTab: () => void
}

const TabBarComponent = forwardRef<HTMLDivElement, TabBarProps>(function TabBar(
  {
    activeTabId,
    playingTabId,
    activeSidebarTrackId,
    isPlaying,
    onSwitchTab,
    onCloseTab,
    onNewTab,
  },
  ref,
) {
  // Use getState to read tabs non-reactively and only update when metadata changes
  // This prevents re-renders when only code changes (not metadata)
  const getTabs = useTabsStore.getState
  const tabsRef = useRef<Array<{ id: string; title: string; artist: string; isDirty: boolean }>>([])
  const prevMetadataKeyRef = useRef<string>('')
  const [, forceUpdate] = useState(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const scrollIntervalRef = useRef<number | null>(null)
  const prevTabsLengthRef = useRef<number>(getTabs().tabs.length)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollState = () => {
    const container = scrollContainerRef.current
    if (!container) return
    setCanScrollLeft(container.scrollLeft > 0)
    setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth - 1)
  }

  const stopScrolling = () => {
    if (scrollIntervalRef.current !== null) {
      clearInterval(scrollIntervalRef.current)
      scrollIntervalRef.current = null
    }
  }

  useEffect(() => {
    const handleGlobalPointerUp = () => {
      stopScrolling()
    }
    window.addEventListener('pointerup', handleGlobalPointerUp)
    return () => {
      stopScrolling()
      window.removeEventListener('pointerup', handleGlobalPointerUp)
    }
  }, [])

  // Check metadata changes and update refs only when metadata actually changes
  useEffect(() => {
    const allTabs = getTabs().tabs
    const metadataKey = allTabs.map(t => `${t.id}|${t.title}|${t.artist}|${t.isDirty}`).join(';')

    // Only update if metadata actually changed
    if (metadataKey !== prevMetadataKeyRef.current) {
      prevMetadataKeyRef.current = metadataKey
      tabsRef.current = allTabs.map(tab => ({
        id: tab.id,
        title: tab.title,
        artist: tab.artist,
        isDirty: tab.isDirty,
      }))
      setTabsLength(allTabs.length)
      forceUpdate(n => n + 1)
      setTimeout(() => updateScrollState(), 0)
    }
  }, [activeTabId, getTabs])

  // Subscribe to store changes but filter to only update on metadata changes
  useEffect(() => {
    const unsubscribe = useTabsStore.subscribe(() => {
      const allTabs = getTabs().tabs
      const metadataKey = allTabs.map(t => `${t.id}|${t.title}|${t.artist}|${t.isDirty}`).join(';')

      // Only update if metadata actually changed (not just code)
      if (metadataKey !== prevMetadataKeyRef.current) {
        prevMetadataKeyRef.current = metadataKey
        tabsRef.current = allTabs.map(tab => ({
          id: tab.id,
          title: tab.title,
          artist: tab.artist,
          isDirty: tab.isDirty,
        }))
        setTabsLength(allTabs.length)
        forceUpdate(n => n + 1)
        setTimeout(() => updateScrollState(), 0)
      }
    })

    return unsubscribe
  }, [getTabs])

  if (tabsRef.current.length === 0) {
    const initialTabs = getTabs().tabs
    tabsRef.current = initialTabs.map(tab => ({
      id: tab.id,
      title: tab.title,
      artist: tab.artist,
      isDirty: tab.isDirty,
    }))
    prevMetadataKeyRef.current = initialTabs
      .map(tab => `${tab.id}|${tab.title}|${tab.artist}|${tab.isDirty}`)
      .join(';')
  }

  const tabs = tabsRef.current
  const [copied, setCopied] = useState(false)
  const [tabsLength, setTabsLength] = useState(() => getTabs().tabs.length)
  const [draggedTabId, setDraggedTabId] = useState<string | null>(null)
  const [draggedTabFromIndex, setDraggedTabFromIndex] = useState<number | null>(null)
  const [draggedTabOffset, setDraggedTabOffset] = useState<number>(0)
  const [targetIndex, setTargetIndex] = useState<number | null>(null)
  const [isDropping, setIsDropping] = useState(false)
  const dragStartXRef = useRef<number>(0)
  const dragStartScrollLeftRef = useRef<number>(0)
  const tabElementsRef = useRef<Map<string, HTMLElement>>(new Map())
  const originalTabPositionsRef = useRef<Map<string, number>>(new Map())
  const reorderTab = useTabsStore(state => state.reorderTab)

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    updateScrollState()

    const handleScroll = () => {
      updateScrollState()
    }

    const resizeObserver = new ResizeObserver(() => {
      updateScrollState()
    })

    container.addEventListener('scroll', handleScroll)
    resizeObserver.observe(container)

    return () => {
      container.removeEventListener('scroll', handleScroll)
      resizeObserver.disconnect()
    }
  }, [tabs.length])

  useEffect(() => {
    if (!draggedTabId) return

    const handlePointerMove = (e: PointerEvent) => {
      if (!draggedTabId || dragStartXRef.current === null) return
      const container = scrollContainerRef.current
      if (!container) return

      const currentX = e.clientX
      const deltaX = currentX - dragStartXRef.current
      const scrollDelta = container.scrollLeft - dragStartScrollLeftRef.current
      const totalOffset = deltaX + scrollDelta

      setDraggedTabOffset(totalOffset)

      const storeTabs = getTabs().tabs
      const draggedTabElement = tabElementsRef.current.get(draggedTabId)
      if (!draggedTabElement) return

      const containerRect = container.getBoundingClientRect()
      const pointerXInContainer = e.clientX - containerRect.left + container.scrollLeft

      let newTargetIndex = draggedTabFromIndex!
      let found = false

      for (let i = 0; i < storeTabs.length; i++) {
        const tab = storeTabs[i]!
        const tabElement = tabElementsRef.current.get(tab.id)
        if (!tabElement) continue

        const originalTabLeft = originalTabPositionsRef.current.get(tab.id) ?? 0
        const tabWidth = tabElement.offsetWidth
        const tabRight = originalTabLeft + tabWidth

        if (tab.id === draggedTabId) continue

        if (pointerXInContainer >= originalTabLeft && pointerXInContainer < tabRight) {
          if (i < draggedTabFromIndex!) {
            newTargetIndex = i
          }
          else if (i > draggedTabFromIndex!) {
            newTargetIndex = i + 1
          }
          else {
            newTargetIndex = draggedTabFromIndex!
          }
          found = true
          break
        }
      }

      if (!found) {
        if (pointerXInContainer < (originalTabPositionsRef.current.get(storeTabs[0]!.id) ?? 0)) {
          newTargetIndex = 0
        }
        else {
          const lastTab = storeTabs[storeTabs.length - 1]
          if (lastTab) {
            const lastTabElement = tabElementsRef.current.get(lastTab.id)
            if (lastTabElement) {
              const lastTabLeft = originalTabPositionsRef.current.get(lastTab.id) ?? 0
              const lastTabRight = lastTabLeft + lastTabElement.offsetWidth
              if (pointerXInContainer >= lastTabRight) {
                newTargetIndex = storeTabs.length
              }
            }
          }
        }
      }

      if (newTargetIndex !== targetIndex) {
        setTargetIndex(newTargetIndex)
      }
    }

    const handlePointerUp = () => {
      if (!draggedTabId || draggedTabFromIndex === null || targetIndex === null) return

      const fromIndex = draggedTabFromIndex
      const toIndex = targetIndex

      if (toIndex !== fromIndex) {
        setIsDropping(true)
        setDraggedTabId(null)
        setDraggedTabFromIndex(null)
        setDraggedTabOffset(0)
        setTargetIndex(null)
        dragStartXRef.current = 0

        reorderTab(fromIndex, toIndex)

        requestAnimationFrame(() => {
          setIsDropping(false)
        })
      }
      else {
        setDraggedTabId(null)
        setDraggedTabFromIndex(null)
        setDraggedTabOffset(0)
        setTargetIndex(null)
        dragStartXRef.current = 0
      }
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)

    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
    }
  }, [draggedTabId, draggedTabFromIndex, targetIndex, getTabs, reorderTab])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container || !activeTabId) return

    const tabsLengthDecreased = tabsLength < prevTabsLengthRef.current
    prevTabsLengthRef.current = tabsLength

    if (!tabsLengthDecreased) return

    requestAnimationFrame(() => {
      // requestAnimationFrame(() => {
      if (!container) return
      const tabElement = container.querySelector(`[data-tab-id="${activeTabId}"]`) as HTMLElement
      if (!tabElement) return

      const scrollButtonsWidth = (canScrollLeft || canScrollRight) ? 64 : 0
      const newTabButtonWidth = 57
      const rightOffset = scrollButtonsWidth + newTabButtonWidth

      const tabLeft = tabElement.offsetLeft
      const tabRight = tabLeft + tabElement.offsetWidth
      const containerLeft = container.scrollLeft
      const containerRight = container.scrollLeft + container.clientWidth - rightOffset

      if (tabLeft < containerLeft) {
        container.scrollLeft = tabLeft
      }
      else if (tabRight > containerRight) {
        container.scrollLeft = tabRight - container.clientWidth + rightOffset
      }
      updateScrollState()
    })
    // })
  }, [activeTabId, tabsLength, canScrollLeft, canScrollRight])

  const scrollSpeed = 20

  const startScrollLeft = () => {
    stopScrolling()
    const container = scrollContainerRef.current
    if (!container) return
    container.scrollBy({ left: -scrollSpeed, behavior: 'auto' })
    scrollIntervalRef.current = window.setInterval(() => {
      if (container.scrollLeft <= 0) {
        stopScrolling()
        return
      }
      container.scrollBy({ left: -scrollSpeed, behavior: 'auto' })
      updateScrollState()
    }, 16)
  }

  const startScrollRight = () => {
    stopScrolling()
    const container = scrollContainerRef.current
    if (!container) return
    const maxScroll = container.scrollWidth - container.clientWidth
    container.scrollBy({ left: scrollSpeed, behavior: 'auto' })
    scrollIntervalRef.current = window.setInterval(() => {
      if (container.scrollLeft >= maxScroll - 1) {
        stopScrolling()
        return
      }
      container.scrollBy({ left: scrollSpeed, behavior: 'auto' })
      updateScrollState()
    }, 16)
  }

  return (
    <div className="relative shrink-0 h-[40px]">
      <div
        ref={el => {
          scrollContainerRef.current = el
          if (typeof ref === 'function') {
            ref(el)
          }
          else if (ref) {
            ref.current = el
          }
        }}
        onPointerDown={e => {
          if (e.button === 1) {
            const target = e.target as HTMLElement
            const isTab = target.closest('[data-tab-id]')
            const isButton = target.closest('button')
            if (!isTab && !isButton) {
              e.preventDefault()
              onNewTab()
            }
          }
        }}
        className="h-[40px] bg-black border-b border-[#444] flex items-center shrink-0 select-none overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map((tab) => {
          const storeTabs = getTabs().tabs
          const storeIndex = storeTabs.findIndex(t => t.id === tab.id)
          const isDragging = draggedTabId === tab.id

          let translateX = 0
          if (draggedTabId && draggedTabFromIndex !== null && targetIndex !== null && !isDragging) {
            const draggedTabElement = tabElementsRef.current.get(draggedTabId)
            const draggedTabWidth = draggedTabElement ? draggedTabElement.offsetWidth : 0

            if (draggedTabFromIndex < targetIndex) {
              if (storeIndex > draggedTabFromIndex && storeIndex < targetIndex) {
                translateX = -draggedTabWidth
              }
            }
            else if (draggedTabFromIndex > targetIndex) {
              if (storeIndex >= targetIndex && storeIndex < draggedTabFromIndex) {
                translateX = draggedTabWidth
              }
            }
          }

          return (
            <div
              key={tab.id}
              data-tab-id={tab.id}
              ref={el => {
                if (el) {
                  tabElementsRef.current.set(tab.id, el)
                }
                else {
                  tabElementsRef.current.delete(tab.id)
                }
              }}
              onPointerDown={e => {
                if (e.button === 1) {
                  e.preventDefault()
                  onCloseTab(tab.id)
                  return
                }
                if (e.button !== 0) return

                const container = scrollContainerRef.current
                if (!container) return

                onSwitchTab(tab.id)

                dragStartXRef.current = e.clientX
                dragStartScrollLeftRef.current = container.scrollLeft
                const fromIndex = getTabs().tabs.findIndex(t => t.id === tab.id)

                const storeTabs = getTabs().tabs
                originalTabPositionsRef.current.clear()
                storeTabs.forEach(t => {
                  const el = tabElementsRef.current.get(t.id)
                  if (el) {
                    originalTabPositionsRef.current.set(t.id, el.offsetLeft)
                  }
                })

                setDraggedTabId(tab.id)
                setDraggedTabFromIndex(fromIndex)
                setTargetIndex(fromIndex)
                e.preventDefault()
              }}
              style={{
                transform: isDragging
                  ? `translateX(${draggedTabOffset}px)`
                  : translateX !== 0
                  ? `translateX(${translateX}px)`
                  : 'translateX(0px)',
                transition: isDragging || isDropping ? 'none' : 'transform 0.2s ease-out',
                zIndex: isDragging ? 11 : activeTabId === tab.id ? 2 : 1,
                position: 'relative',
              }}
              className={`h-full px-4 flex items-center justify-between gap-2 border-r border-[#444] whitespace-nowrap flex-shrink-0 ${
                activeTabId === tab.id
                  ? 'bg-gradient-to-b from-neutral-600 to-neutral-900 text-white'
                  : 'bg-gradient-to-b from-black to-neutral-800 text-[#888] hover:bg-[#252525]'
              } cursor-pointer`}
            >
              <div className="flex items-center gap-2 flex-1">
                {isPlaying && playingTabId === tab.id && activeSidebarTrackId === null && (
                  <SpeakerHighIcon weight="fill" size={14} className="text-orange-500 shrink-0" />
                )}
                <div className="flex items-center gap-1 min-w-0 relative top-[-1px]">
                  <div className="min-w-0">
                    <span className="text-sm truncate">
                      {tab.artist ? `${tab.artist} - ` : ''}
                      {tab.title}
                    </span>
                  </div>
                </div>
                {tab.isDirty && (
                  <div
                    title="Has unsaved changes"
                    className="w-1.5 h-1.5 rounded-full bg-white shrink-0"
                  />
                )}
              </div>
              <button
                onPointerDown={e => {
                  e.stopPropagation()
                  if (e.button === 1) {
                    onCloseTab(tab.id)
                  }
                }}
                onClick={e => {
                  e.stopPropagation()
                  onCloseTab(tab.id)
                }}
                className="text-[#888] hover:text-white hover:bg-[#444] rounded px-1 flex items-center shrink-0"
              >
                <XIcon size={16} />
              </button>
            </div>
          )
        })}
        <div className="h-full sticky right-0 flex items-center shrink-0 z-10">
          {(canScrollLeft || canScrollRight) && (
            <>
              <button
                onPointerDown={e => {
                  e.preventDefault()
                  startScrollLeft()
                }}
                onPointerUp={stopScrolling}
                onPointerCancel={stopScrolling}
                onPointerLeave={stopScrolling}
                className="h-full bg-black px-2 flex items-center text-[#888] hover:text-white hover:bg-[#252525] border-r border-[#444] shrink-0 z-10"
                title="Scroll left"
              >
                <CaretLeftIcon size={16} weight="bold" />
              </button>
              <button
                onPointerDown={e => {
                  e.preventDefault()
                  startScrollRight()
                }}
                onPointerUp={stopScrolling}
                onPointerCancel={stopScrolling}
                onPointerLeave={stopScrolling}
                className="h-full bg-black px-2 flex items-center text-[#888] hover:text-white hover:bg-[#252525] border-r border-[#444] shrink-0 z-10"
                title="Scroll right"
              >
                <CaretRightIcon size={16} weight="bold" />
              </button>
            </>
          )}
          <button
            onClick={onNewTab}
            className="h-full bg-black px-4 flex items-center text-[#888] hover:text-white hover:bg-[#252525] border-r border-[#444] shrink-0 z-10"
            title="New Tab"
          >
            <PlusIcon size={16} />
          </button>
        </div>
      </div>
    </div>
  )
})

TabBarComponent.displayName = 'TabBar'

// Wrap in memo to prevent reconciliation when parent re-renders
// Props are already stable (callbacks are memoized in App)
export const TabBar = memo(TabBarComponent)
